import FlightIcon from '@mui/icons-material/Flight'
import HotelIcon from '@mui/icons-material/Hotel'
import React from 'react'
import { HOLIDAYS_NAV_PATH } from '@/src/utils/holidays'

// ============================================================================
// Types & Interfaces
// ============================================================================

/** User menu item interface */
export interface UserMenuItem {
    label: string
    href: string
}

/** Navigation link interface */
export interface NavLink {
    label: string
    href: string
    labelKey?: string
    icon?: React.ReactNode
    isActive?: boolean
}

/** Header visibility flags interface */
export interface HeaderVisibility {
    showLangCurrDropdown: boolean
    showDeals: boolean
    shouldHideSignIn: boolean
    isOffersPage: boolean
    isItineraryPage: boolean
    isSignInPage: boolean
}

/** User object interface for display name extraction */
export interface User {
    firstName?: string
    displayName?: string
    email?: string
}

// ============================================================================
// Support URL Utilities
// ============================================================================

export const getSupportHref = (locale: string): string => `/${locale}/contact`

export const getContactSupportHref = (locale: string): string => getSupportHref(locale)

// ============================================================================
// User Menu Utilities
// ============================================================================

/** Generates user menu items for header (Flyin) */
export const getUserMenuItems = (t: (key: string) => string, locale: string): UserMenuItem[] => {
    return [
        { label: t('userMenu.myBookings'), href: `/${locale}/trips` },
        { label: t('userMenu.myWallet'), href: `/${locale}/wallet` },
        { label: t('userMenu.profile'), href: `/${locale}/profile` },
        { label: t('userMenu.travellers'), href: `/${locale}/travellers` },
        { label: t('userMenu.myReviews'), href: '/manageReviews' }
    ]
}

/** Cleartrip user menu: Trips, Travellers, Profile, Wallet (settings disabled) */
export const getCTUserMenuItems = (t: (key: string) => string, locale: string): UserMenuItem[] => {
    return [
        { label: t('userMenu.trips'), href: `/${locale}/trips` },
        { label: t('userMenu.travellers'), href: `/${locale}/travellers` },
        { label: t('userMenu.profile'), href: `/${locale}/profile` },
        { label: t('userMenu.wallet'), href: `/${locale}/wallet` },
        { label: t('userMenu.expressway'), href: '/accounts/expressway' },
        { label: t('userMenu.settings'), href: `/account/settings` }
    ]
}

/** Gets user display name with fallback logic (firstName → displayName → email username → 'User') */
export const getUserDisplayName = (user: User): string => {
    if (user.firstName) {
        return user.firstName
    }

    if (user.displayName) {
        return user.displayName
    }

    if (user.email) {
        return user.email.split('@')[0]
    }

    return 'User'
}

// ============================================================================
// Navigation Utilities
// ============================================================================

/** Generates navigation links for Flyin header variant, with the active vertical flagged.
 * The visa/flights-hotels slot is omitted entirely when neither is enabled (e.g. Heya).
 * Experience is a Heya-only vertical, shown after the others when enabled. */
export const getFlyinNavLinks = (
    locale: string,
    activeVertical: string | null = null,
    showVisa: boolean = false,
    showFlightsHotels: boolean = true,
    showExperience: boolean = false
): NavLink[] => {
    const localePrefix = `/${locale}`

    const links: NavLink[] = [
        { label: 'Flights', href: `${localePrefix}/flights`, isActive: activeVertical === 'flights' },
        { label: 'Hotels', href: `${localePrefix}/hotels`, isActive: activeVertical === 'hotels' }
    ]

    if (showVisa) {
        links.push({ label: 'visa', href: '/visa', isActive: activeVertical === 'visa' })
    } else if (showFlightsHotels) {
        links.push({ label: 'fph', href: `${localePrefix}/flights-hotels`, isActive: activeVertical === 'fph' })
    }

    if (showExperience) {
        links.push({ label: 'experience', href: `${localePrefix}/experience`, isActive: activeVertical === 'experience' })
    }

    return links
}

/** Generates navigation links for Cleartrip header variant with active state (Visa href has no locale).
 * Visa and Holidays links are only shown when their flag is true (Cleartrip AE domain only). */
export const getCTNavLinks = (
    locale: string,
    activeVertical: string | null,
    showVisa: boolean = false,
    showHolidays: boolean = false
): NavLink[] => {
    const localePrefix = `/${locale}`

    const links: NavLink[] = [
        {
            label: 'Flights',
            href: `${localePrefix}/flights`,
            isActive: activeVertical === 'flights'
        },
        {
            label: 'Hotels',
            href: `${localePrefix}/hotels`,
            isActive: activeVertical === 'hotels'
        }
    ]
    if (showVisa) {
        links.push({
            label: 'Visa',
            href: '/visa',
            isActive: activeVertical === 'visa'
        })
    }
    if (showHolidays) {
        links.push({
            label: 'Holidays',
            href: `${localePrefix}${HOLIDAYS_NAV_PATH}`,
            isActive: activeVertical === 'holidays'
        })
    }
    return links
}

/** Gets icon component for navigation item label (case-insensitive) */
export const getNavIcon = (label: string): React.ReactNode | null => {
    const lowerLabel = label.toLowerCase()
    if (lowerLabel === 'flights') {
        return React.createElement(FlightIcon, { sx: { fontSize: 20, color: 'inherit' } })
    }
    if (lowerLabel === 'hotels') {
        return React.createElement(HotelIcon, { sx: { fontSize: 20, color: 'inherit' } })
    }
    return null
}

/** Transforms label to translation key for Flyin header navigation links */
export const getTranslationKey = (label: string, labelKey?: string): string => {
    if (labelKey) {
        return labelKey
    }

    return label
        .replace(/ \+ /g, 'Plus')
        .replace(/ /g, '')
        .replace(/^[A-Z]/, c => c.toLowerCase())
}

// ============================================================================
// Visibility Utilities
// ============================================================================

/** Determines visibility flags for header elements based on current pathname and variant */
export const getHeaderVisibility = (
    pathname: string,
    variant: 'flyin' | 'cleartrip' = 'flyin',
    showOffers = true
): HeaderVisibility => {
    const matchers = {
        homepage: (path: string) => path === '/' || /^\/[a-z]{2}$/.test(path),
        flights: (path: string) => path === '/flights' || /^\/[a-z]{2}\/flights\/?$/.test(path),
        srp: (path: string) => path === '/flights/results' || /^\/[a-z]{2}\/flights\/results/.test(path),
        offers: (path: string) => path.includes('/offers'),
        airlines: (path: string) => path.includes('/airlines'),
        itinerary: (path: string) => path.includes('/flights/itinerary'),
        signin: (path: string) => path.includes('/signin'),
        flightSchedule: (path: string) => path.includes('/flight-schedule'),
        // `/hotels/itinerary` also covers the confirmation step, which now lives at
        // /hotels/itinerary/{id}/confirmation.
        hotels: (path: string) => path.includes('/hotels') && !path.includes('/hotels/itinerary'),
        hotelDetails: (path: string) => path.includes('/hotels/details'),
        experience: (path: string) => path === '/experience' || /^\/[a-z]{2}\/experience\/?$/.test(path)
    }

    const pageFlags = {
        homepage: matchers.homepage(pathname),
        flights: matchers.flights(pathname),
        srp: matchers.srp(pathname),
        offers: matchers.offers(pathname),
        airlines: matchers.airlines(pathname),
        itinerary: matchers.itinerary(pathname),
        signin: matchers.signin(pathname),
        flightSchedule: matchers.flightSchedule(pathname),
        hotels: matchers.hotels(pathname),
        hotelDetails: matchers.hotelDetails(pathname),
        experience: matchers.experience(pathname)
    }

    const visibleByVariant = {
        showLangCurrDropdown: {
            flyin: ['homepage', 'flights', 'srp', 'offers', 'airlines', 'flightSchedule', 'hotels', 'experience'],
            cleartrip: ['homepage', 'flights', 'offers', 'srp', 'airlines', 'hotels']
        },
        showDeals: {
            flyin: ['homepage', 'flights', 'srp', 'airlines', 'hotels'],
            cleartrip: ['homepage', 'flights', 'offers', 'srp', 'airlines', 'hotels']
        }
    } as const

    const isInGroup = (group: readonly string[]) => group.some(key => pageFlags[key as keyof typeof pageFlags])

    // Hotel details prices one country and currency at a time, so offering to switch either
    // would contradict the rates on the page. `hotels` covers details in the groups above, so
    // it is excluded here rather than by splitting that flag. Applies to both variants.
    const showLangCurrDropdown =
        isInGroup(visibleByVariant.showLangCurrDropdown[variant]) && !pageFlags.hotelDetails
    // The Deals link points at /offers, so it follows the OFFERS feature.
    const showDeals = isInGroup(visibleByVariant.showDeals[variant]) && showOffers
    const shouldHideSignIn = pageFlags.itinerary || pageFlags.signin || pageFlags.offers

    return {
        showLangCurrDropdown,
        showDeals,
        shouldHideSignIn,
        isOffersPage: pageFlags.offers,
        isItineraryPage: pageFlags.itinerary,
        isSignInPage: pageFlags.signin
    }
}
